import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  Badge,
  Button,
  EmptyState,
  Pagination,
  ViewToggle,
  type ViewMode,
} from "../../../../components/ui";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useDarkMode } from "../../../../context/ThemeContext";
import { panelThemeClasses } from "../../../../theme/themeClasses";
import { qk } from "../../../../lib/queryKeys";
import { useRoutesList } from "../../../../features/routes/queries";
import {
  useRoutesStore,
  type Route,
} from "../../../../store/admin/routesStore";
import {
  useCompaniesStore,
  useDisableCRUD,
} from "../../../../store/admin/companiesStore";
import { useStatusesStore } from "../../../../store/admin/statusesStore";
import {
  getStatusBadgeLabel,
  getStatusBadgeStyle,
  getStatusTone,
} from "../../../../utils/getStatusEntity";
import {
  RouteDetailsModal,
  RouteFilters,
  RouteModal,
  useRouteForm,
  type RouteFormValues,
} from "../../../admin/components/sections/routesComponents";
import RoutesCard from "../../../admin/components/Routes/RoutesCard";

const PAGE_SIZE = 6;

const formatDuration = (minutes: number | undefined): string => {
  if (!minutes || minutes <= 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export default function RutasView() {
  const { t } = useTranslation("envios");
  const { t: tAdmin } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailsRoute, setDetailsRoute] = useState<Route | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editRoute, setEditRoute] = useState<Route | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const companyId = useCompaniesStore((s) => s.companyId);
  const disableCRUD = useDisableCRUD();
  const createRoute = useRoutesStore((s) => s.createRoute);
  const updateRoute = useRoutesStore((s) => s.updateRoute);
  const statuses = useStatusesStore((s) => s.statuses);
  const fetchStatuses = useStatusesStore((s) => s.fetchStatuses);

  const debouncedSearch = useDebounce(search);
  const themeClasses = panelThemeClasses();

  const isEdit = !!editRoute;

  const {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  } = useRouteForm({ isEdit, editRoute });

  useEffect(() => {
    if (statuses.length === 0) fetchStatuses({ limit: 200, page: 1 });
  }, [statuses.length, fetchStatuses]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  const params = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status_id: statusFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    [page, statusFilter, debouncedSearch],
  );

  const { data, isLoading, isError } = useRoutesList(params);
  const routes = data?.data ?? [];
  const totalPages = data?.meta?.totalPages || 1;

  const invalidateRoutes = () => {
    queryClient.invalidateQueries({ queryKey: ["routes"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditRoute(null);
    setLoadingSubmit(false);
    resetForm();
  };

  const handleAddNew = () => {
    setEditRoute(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback(
    (route: Route) => {
      if (disableCRUD) return;
      setEditRoute(route);
      setTimeout(() => setFormOpen(true), 0);
    },
    [disableCRUD],
  );

  const handleCreate = async (values: RouteFormValues) => {
    if (!companyId) {
      toast.error(t("routes.messages.noCompanySelected"));
      return;
    }

    setLoadingSubmit(true);
    const result = await createRoute({
      ...prepareRouteData(values),
      company_id: companyId,
    });
    setLoadingSubmit(false);

    if (!result.success) {
      toast.error(result.error || t("routes.messages.createError"));
      return;
    }

    toast.success(t("routes.messages.created", { name: values.name }));
    invalidateRoutes();
    handleCloseForm();
  };

  const handleUpdate = async (values: RouteFormValues) => {
    if (!editRoute) return;

    setLoadingSubmit(true);
    const result = await updateRoute(editRoute.id, prepareRouteData(values));
    setLoadingSubmit(false);

    if (!result.success) {
      toast.error(result.error || t("routes.messages.updateError"));
      return;
    }

    toast.success(t("routes.messages.updated", { name: values.name }));
    invalidateRoutes();
    handleCloseForm();
  };

  const clearFilters = () => {
    setStatusFilter("");
    setSearch("");
  };

  return (
    <div className="space-y-5 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-content">{t("routes.title")}</h1>
          <p className="mt-1 text-sm text-content-secondary">{t("routes.subtitle")}</p>
        </div>
        <CRUDTooltip className="shrink-0">
          <Button
            size="sm"
            icon={<i className="ri-add-line" />}
            disabled={disableCRUD}
            onClick={handleAddNew}
          >
            {t("routes.newRoute")}
          </Button>
        </CRUDTooltip>
      </div>

      <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
        <RouteFilters
          status_id={statusFilter}
          searchQuery={search}
          themeClasses={themeClasses}
          onStatusChange={(value) => setStatusFilter(value || "")}
          onSearchChange={(event) => setSearch(event.target.value)}
        />
        <ViewToggle
          value={viewMode}
          onChange={setViewMode}
          labels={{ grid: t("routes.view.grid"), list: t("routes.view.list") }}
        />
      </div>

      {isLoading ? (
        <div className="flex min-h-64 items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      ) : isError ? (
        <EmptyState icon="ri-error-warning-line" title={t("routes.messages.loadError")} />
      ) : routes.length === 0 ? (
        <EmptyState
          icon="ri-route-line"
          title={t("routes.empty.title")}
          description={t("routes.empty.description")}
          action={
            <Button variant="link" size="sm" onClick={clearFilters}>
              {t("routes.empty.clearFilters")}
            </Button>
          }
        />
      ) : (
        <>
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {routes.map((route) => (
                <RoutesCard
                  key={route.id}
                  route={route}
                  themeClasses={themeClasses}
                  onDetailsClick={setDetailsRoute}
                  onEditClick={handleEdit}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {routes.map((route) => (
                <div
                  key={route.id}
                  className="flex flex-col gap-3 rounded-xl border border-line bg-card p-4 transition-colors hover:border-primary-500/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                      <i className="ri-route-line text-lg" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold text-content">
                          {route.name}
                        </h3>
                        <Badge
                          tone={getStatusTone(route.status)}
                          style={getStatusBadgeStyle(route.status)}
                        >
                          {getStatusBadgeLabel(
                            route.status,
                            tAdmin,
                            tAdmin("units.status.unknown"),
                          )}
                        </Badge>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-secondary">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line" />
                          {formatDuration(route.principal_route_duration)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-road-map-line" />
                          {(route.principal_route_distance ?? 0).toFixed(2)} km
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-map-pin-line" />
                          {t("routes.fields.pointsCount", {
                            count: route.principal_route?.length || 0,
                          })}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-truck-line" />
                          {route.vehicles_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(route)}>
                      {t("routes.actions.edit")}
                    </Button>
                    <Button size="sm" onClick={() => setDetailsRoute(route)}>
                      {t("routes.actions.details")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <RouteModal
        isOpen={formOpen}
        isEdit={isEdit}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        routePoints={routePoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        hasRealChanges={hasRealChanges}
        onCoordinateEdit={handleCoordinateEdit}
        onAddressEdit={handleAddressEdit}
        onAddPoint={handleAddPoint}
        onRemovePoint={handleRemovePoint}
        onMapPointsChange={handleMapPointsChange}
        onRouteCalculated={handleRouteCalculated}
        onAxisCalculated={handleAxisCalculated}
        onCorridorBufferChange={handleBufferChange}
        onSubmit={isEdit ? handleUpdate : handleCreate}
        onClose={handleCloseForm}
      />

      <RouteDetailsModal
        isOpen={!!detailsRoute}
        route={detailsRoute}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        onClose={() => setDetailsRoute(null)}
        onEdit={(route) => {
          setDetailsRoute(null);
          handleEdit(route);
        }}
      />
    </div>
  );
}
